import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { MixedText } from '../../utils/rtl';
import { Button } from '../ui/Button';

interface RestrictedAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  description?: string;
}

const RestrictedAccessModal: React.FC<RestrictedAccessModalProps> = ({ 
  isOpen, 
  onClose,
  title,
  message,
  description
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[71] flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 pointer-events-auto animate-in fade-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`flex justify-between items-start mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <h3 className={`text-xl font-bold text-surface-primary ${isRTL ? 'text-right' : ''}`}>
              <MixedText>{title || t('common:labels.feature_locked', 'Feature Locked')}</MixedText>
            </h3>
            <button 
              onClick={onClose}
              className="text-surface-secondary hover:text-surface-primary transition-colors p-1 hover:bg-surface-subtle rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="text-center py-6">
            <div className="w-24 h-24 mx-auto mb-4">
              <img 
                src="/astronaut_fat.png" 
                alt="Locked feature astronaut" 
                className="w-full h-full object-contain drop-shadow-lg"
              />
            </div>
            <p className="text-lg font-medium text-surface-primary mb-2">
              <MixedText>{message || t('common:messages.registered_only', 'Only for registered users')}</MixedText>
            </p>
            <p className="text-surface-secondary mb-6 text-sm">
              <MixedText>{description || t('common:messages.signup_to_chat', 'Sign up to chat with your study materials and get personalized AI help.')}</MixedText>
            </p>
            
            <div className="flex flex-col gap-3">
              <Button 
                variant="primary" 
                onClick={() => navigate('/register')}
                className="w-full justify-center"
              >
                <MixedText>{t('common:navigation.signup_free', 'Sign Up Free')}</MixedText>
              </Button>
              <Button 
                variant="ghost" 
                onClick={onClose}
                className="w-full justify-center"
              >
                <MixedText>{t('common:buttons.cancel', 'Cancel')}</MixedText>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RestrictedAccessModal;

